// API Client for communicating with the backend

// Use relative path in development, full URL in production (Pages)
const API_BASE = window.location.hostname.includes('pages.dev') || window.location.hostname.includes('localhost') === false
  ? 'https://aged-domains-marketplace.ijuwebdesk.workers.dev/api'
  : '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface AuthResponse {
  token: string;
  user: {
    id: number;
    email: string;
  };
}

interface DomainWithAge {
  domain_name: string;
  listing_type: 'auction' | 'buy-now' | 'both';
  current_price: number | null;
  buy_now_price: number | null;
  auction_end_date: string | null;
  registration_date: string | null;
  tld: string;
  active: number;
  source: string;
  last_updated: string;
  age_years: number | null;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface DomainFilters {
  search?: string;
  tld?: string;
  listing_type?: string;
  min_price?: number;
  max_price?: number;
  min_age?: number;
  max_age?: number;
  sort?: string;
  order?: string;
  page?: number;
  limit?: number;
}

interface SellTemplate {
  id: string;
  name: string;
  description: string;
}

interface SellPage {
  id: number;
  user_id: number;
  domain_name: string;
  price: number;
  contact_email: string;
  template_id: string;
  status: 'active' | 'draft';
  show_stats_image: number;
  stats_image_key: string | null;
  created_at: string;
  updated_at: string;
}

interface PortfolioItem {
  id: number;
  user_id: number;
  domain_name: string;
  purchase_price: number;
  purchase_date: string;
  yearly_renewal_price: number;
  is_sold: number;
  sale_price: number | null;
  sale_date: string | null;
  created_at: string;
  updated_at: string;
  renewal_years: number;
  renewal_cost: number;
  total_cost_basis: number;
  current_value: number;
  profit: number;
}

interface PortfolioSummary {
  total_domains: number;
  sold_domains: number;
  unsold_domains: number;
  total_purchase_cost: number;
  total_renewal_cost: number;
  total_cost_basis: number;
  realized_value: number;
  unrealized_value: number;
  total_portfolio_value: number;
  total_profit: number;
  roi_percent: number;
}

interface PortfolioResponse {
  items: PortfolioItem[];
  summary: PortfolioSummary;
}

interface PortfolioPayload {
  domain_name: string;
  purchase_price: number;
  purchase_date: string;
  yearly_renewal_price: number;
  is_sold: boolean;
  sale_price: number | null;
  sale_date: string | null;
}

class ApiClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP error ${response.status}`,
        };
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  // Auth endpoints
  async signup(email: string, password: string): Promise<ApiResponse<AuthResponse>> {
    const response = await this.request<AuthResponse>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.success && response.data) {
      this.setToken(response.data.token);
    }

    return response;
  }

  async login(email: string, password: string): Promise<ApiResponse<AuthResponse>> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.success && response.data) {
      this.setToken(response.data.token);
    }

    return response;
  }

  logout() {
    this.setToken(null);
  }

  async getMe(): Promise<ApiResponse<{ user: { id: number; email: string } }>> {
    return this.request('/auth/me');
  }

  // Domain endpoints
  async listDomains(filters: DomainFilters = {}): Promise<ApiResponse<PaginatedResponse<DomainWithAge>>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    });

    const queryString = params.toString();
    const endpoint = queryString ? `/domains?${queryString}` : '/domains';

    return this.request(endpoint);
  }

  async getDomain(domainName: string): Promise<ApiResponse<DomainWithAge>> {
    return this.request(`/domains/${encodeURIComponent(domainName)}`);
  }

  // Health check
  async health(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return this.request('/health');
  }

  // Sell pages
  async listSellTemplates(): Promise<ApiResponse<SellTemplate[]>> {
    return this.request('/sell/templates');
  }

  async listSellPages(): Promise<ApiResponse<SellPage[]>> {
    return this.request('/sell/pages');
  }

  async createSellPage(payload: {
    domain_name: string;
    price: number;
    contact_email: string;
    template_id: string;
    status: 'active' | 'draft';
    show_stats_image: boolean;
    stats_image_key: string | null;
  }): Promise<ApiResponse<SellPage>> {
    return this.request('/sell/pages', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateSellPage(
    id: number,
    payload: {
      domain_name: string;
      price: number;
      contact_email: string;
      template_id: string;
      status: 'active' | 'draft';
      show_stats_image: boolean;
      stats_image_key: string | null;
    }
  ): Promise<ApiResponse<SellPage>> {
    return this.request(`/sell/pages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteSellPage(id: number): Promise<ApiResponse<{ deleted: boolean; id: number }>> {
    return this.request(`/sell/pages/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadSellStatsImage(file: File): Promise<ApiResponse<{ key: string }>> {
    const formData = new FormData();
    formData.append('image', file);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(`${API_BASE}/sell/upload-stats-image`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP error ${response.status}`,
        };
      }
      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async listPortfolio(): Promise<ApiResponse<PortfolioResponse>> {
    return this.request('/portfolio');
  }

  async createPortfolioItem(payload: PortfolioPayload): Promise<ApiResponse<PortfolioItem>> {
    return this.request('/portfolio', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updatePortfolioItem(id: number, payload: PortfolioPayload): Promise<ApiResponse<PortfolioItem>> {
    return this.request(`/portfolio/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }
}

export const api = new ApiClient();
export type {
  DomainWithAge,
  PaginatedResponse,
  DomainFilters,
  AuthResponse,
  ApiResponse,
  SellTemplate,
  SellPage,
  PortfolioItem,
  PortfolioSummary,
  PortfolioResponse,
  PortfolioPayload,
};
